// frontend/src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  LogOut, LayoutDashboard, Database, Plus, Play, Pause, Activity, Clock, Server,
  Loader2, AlertTriangle, CheckCircle2, Settings2, History, TrendingUp,
  ChevronLeft, ChevronRight, XCircle, Filter, Globe,
  RefreshCw, ArrowUpDown, Copy, GitBranch, ArrowUp, ArrowDown,
  FolderKanban, Menu, Download, RotateCcw, ShieldAlert, HeartPulse,
  GitCommitHorizontal, BarChart3,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Modal } from '../components/Modal';
import { PipelineHistory } from './PipelineHistory';
import { formatCompact, formatTime, formatUnixDate, translateCron } from '../utils/formatters';
import { useLanguageStore } from '../store/useLanguageStore';
import { LanguageSelector } from '../components/LanguageSelector';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';
import { AppSidebar } from '../components/AppSidebar';
import { useUIStore } from '../store/useUIStore';
import {
  buildExportFromSaved, downloadJSON, downloadExcel,
} from '../utils/mappingIO';
import { DataLineageModal } from '../components/DataLineage';
import { ProfileModal } from '../components/ProfileModal';

// ── Iconos de dependencia pipeline ────────────────────────────────
// IconParentOf: persona sola → "soy padre, tengo hijos que me esperan"
const IconParentOf = ({ className = "" }) => (
  <svg viewBox="0 0 20 20" className={`shrink-0 ${className}`} fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="5" r="3" />
    <path d="M4 18c0-3.314 2.686-6 6-6s6 2.686 6 6" />
  </svg>
);

// IconChildOf: persona grande + pequeña → "soy hijo, espero a mis padres"
const IconChildOf = ({ className = "" }) => (
  <svg viewBox="0 0 26 20" className={`shrink-0 ${className}`} fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    {/* Persona grande (padre) */}
    <circle cx="8" cy="5" r="3" />
    <path d="M2 18c0-3.314 2.686-6 6-6s6 2.686 6 6" />
    {/* Persona pequeña (yo, el hijo) */}
    <circle cx="20" cy="7.5" r="2.2" />
    <path d="M15 18c0-2.76 2.24-5 5-5s5 2.24 5 5" />
  </svg>
);

// ── 🩺 S3 Health Score — Badge visual ──────────────────────────────
const HealthBadge = ({ data, t }) => {
  if (!data) return <span className="text-[9px] text-slate-300 italic">—</span>;

  const { score, components, totalRuns } = data;

  let color, bg, border, icon;
  if (score >= 80) {
    color = 'text-emerald-700'; bg = 'bg-emerald-50'; border = 'border-emerald-200';
    icon = <HeartPulse className="w-3.5 h-3.5 text-emerald-500" />;
  } else if (score >= 50) {
    color = 'text-amber-700'; bg = 'bg-amber-50'; border = 'border-amber-200';
    icon = <HeartPulse className="w-3.5 h-3.5 text-amber-500" />;
  } else {
    color = 'text-red-700'; bg = 'bg-red-50'; border = 'border-red-200';
    icon = <HeartPulse className="w-3.5 h-3.5 text-red-500" />;
  }

  const tooltipLines = [
    `${t('dashboard.health.success_rate') || 'Tasa éxito'}: ${components.successRate}%`,
    `${t('dashboard.health.sync_check') || 'Verificación'}: ${components.syncCheck}%`,
    `${t('dashboard.health.drift') || 'Schema drift'}: ${components.drift}%`,
    `${t('dashboard.health.schedule') || 'Programación'}: ${components.schedule}%`,
    `${t('dashboard.health.streak') || 'Racha'}: ${components.streak}%`,
    `—`,
    `${t('dashboard.health.runs_7d') || 'Ejecuciones 7D'}: ${totalRuns}`,
  ].join('\n');

  return (
    <div
      className={`inline-flex items-center gap-1 px-1.5 py-1 rounded border ${bg} ${border} cursor-help`}
      title={tooltipLines}
    >
      {icon}
      <span className={`text-xs font-black ${color}`}>{score}</span>
    </div>
  );
};

const handleExportMapping = async (pipelineId, pipelineName, format = 'json') => {
  try {
    const res = await apiClient.get(`/onboarding/migrations/${pipelineId}`);
    if (!res.data.success) return;

    const { mapping, parameters } = res.data.data;
    const srcTables = parameters?.source?.selectedTables || [];
    const tgtTables = parameters?.target?.selectedTables || [];
    const mode = (srcTables.length > 1 || tgtTables.length > 1) ? 'N:M' : '1:1';

    const payload = buildExportFromSaved(mapping, pipelineName, mode);
    const safeName = pipelineName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'mapping';

    if (format === 'excel') {
      downloadExcel(payload, `datiaflow_mapping_${safeName}`);
    } else {
      downloadJSON(payload, `datiaflow_mapping_${safeName}`);
    }
  } catch (err) {
    console.error('Export mapping error:', err);
    // Mostrar error al usuario via tu patrón habitual de toast/modal
  }
};

const handleOpenLineage = async (pipelineId, apiClient, setLineageModal, t, setModalConfig, closeModal) => {
  try {
    const res = await apiClient.get(`/dashboard/lineage/${pipelineId}`);
    if (res.data.success) {
      setLineageModal({ data: res.data });
    }
  } catch (err) {
    console.error('Error cargando lineage:', err);
    setModalConfig({
      isOpen: true,
      title: t('common.error') || 'Error',
      message: err.message || t('lineage.error_loading') || 'No se pudo cargar el lineage de este pipeline.',
      showCancel: false,
      onAccept: closeModal,
    });
  }
};

export const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { t, language } = useLanguageStore();
  const [dependsOnModal, setDependsOnModal] = useState(null); // {pipeline: row}
  const [driftModal, setDriftModal] = useState(null); // { name, schemaDrift }
  const [pendingDependsOnIds, setPendingDependsOnIds] = useState([]); // ← array
  const [pendingFailMode, setPendingFailMode] = useState('normal'); // 'strict'|'normal'|'permissive'
  const [showDepTip, setShowDepTip] = useState(false);
  const [lineageModal, setLineageModal] = useState(null); // { data } o null
  const [profileModal, setProfileModal] = useState(null);

  const [pipelines, setPipelines] = useState([]);
  const [subscription, setSubscription] = useState({ planName: t('common.loading'), quota: 0 });
  const [kpis, setKpis] = useState({ current7d: { success: 0, errors: 0, timeSec: 0 }, prev7d: { success: 0, errors: 0, timeSec: 0 } });
  const [clientName, setClientName] = useState(t('common.loading') || 'Cargando...');

  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [error, setError] = useState(null);

  // ── RES.4: Progreso en tiempo real ─────────────────────────────────
  const [progressMap, setProgressMap] = useState({});  // { pipelineId: progressData }

  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('vnext_dash_status') || 'all');
  const [sortBy, setSortBy] = useState(() => sessionStorage.getItem('vnext_dash_sort') || 'nextExecution');
  const [sortAsc, setSortAsc] = useState(false); // false = DESC (default)
  const [currentPage, setCurrentPage] = useState(() => parseInt(sessionStorage.getItem('vnext_dash_page')) || 1);
  const { openSidebar } = useUIStore();

  useEffect(() => {
    if (!dependsOnModal) return;
    // Inicializar con el valor actual al abrir
    setPendingDependsOnIds(dependsOnModal.pipeline.dependsOn || []);
    const depConfig = dependsOnModal.pipeline.dependsOnConfig || {};
    setPendingFailMode(depConfig.failMode || 'normal');
    setShowDepTip(false);

    // Cerrar con ESC
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setDependsOnModal(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dependsOnModal]);

  useEffect(() => {
    sessionStorage.setItem('vnext_dash_status', statusFilter);
    sessionStorage.setItem('vnext_dash_sort', sortBy);
    sessionStorage.setItem('vnext_dash_page', currentPage);
  }, [statusFilter, sortBy, currentPage]);

  const itemsPerPage = 5;

  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState({ pipeline: null, logs: [] });
  const [isRefreshingHistory, setIsRefreshingHistory] = useState(false);

  const handleRefreshHistory = async () => {
    if (!historyData.pipeline) return;
    setIsRefreshingHistory(true);
    try {
      const res = await apiClient.get(`/dashboard/pipelines/${historyData.pipeline.idConfigHead}/history`);
      if (res.data.success) {
        setHistoryData({
          pipeline: res.data.pipeline,
          logs: res.data.history
        });
      }
    } catch (err) {
      console.error("Error refrescando historial", err);
    } finally {
      setIsRefreshingHistory(false);
    }
  };

  const [modalConfig, setModalConfig] = useState({
    isOpen: false, title: '', message: '', showCancel: false, onAccept: null
  });

  const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));

  // useEffect(() => {
  //   if (user?.role === 1) navigate('/admin/dashboard');
  // }, [user, navigate]);

  const fetchPipelines = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/dashboard/pipelines');
      if (response.data.success) {
        setPipelines(response.data.data);
        setSubscription(response.data.subscription);
        if (response.data.clientName) setClientName(response.data.clientName);
        if (response.data.kpis) setKpis(response.data.kpis);
      }
    } catch (err) {
      setError(err.message || t('dashboard.errors.load_pipelines'));
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect(() => {
  //   if (user?.role !== 1) fetchPipelines();
  // }, [user]);
  useEffect(() => {
    fetchPipelines();
  }, [user]);

  // ── RES.4: Polling de progreso para pipelines en ejecución ─────────
  useEffect(() => {
    const runningIds = pipelines.filter(p => p.isRunning === 1).map(p => p.id);
    if (runningIds.length === 0) {
      setProgressMap({});
      return;
    }

    const fetchProgress = async () => {
      const updates = {};
      for (const id of runningIds) {
        try {
          const res = await apiClient.get(`/dashboard/progress/${id}`);
          updates[id] = res.data;
        } catch {
          updates[id] = { running: false };
        }
      }
      setProgressMap(prev => ({ ...prev, ...updates }));

      // Si algún pipeline dejó de correr, refrescar lista completa
      const anyFinished = Object.values(updates).some(u => !u.running);
      if (anyFinished) {
        setTimeout(() => fetchPipelines(), 1000);
      }
    };

    fetchProgress();
    const interval = setInterval(fetchProgress, 5000);
    return () => clearInterval(interval);
  }, [pipelines.filter(p => p.isRunning === 1).map(p => p.id).join(',')]);

  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  const handleToggleStatus = async (pipelineId) => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      const response = await apiClient.patch(`/dashboard/pipelines/${pipelineId}/toggle`);
      if (response.data.success) {
        setPipelines(prev => prev.map(p => p.id === pipelineId ? { ...p, status: response.data.new_status } : p));
      }
    } catch (err) {
      setModalConfig({
        isOpen: true,
        title: t('common.error') || 'Error',
        message: err.message || t('dashboard.errors.toggle_status') || 'No se pudo cambiar el estado del pipeline.',
        showCancel: false,
        onAccept: closeModal
      });
    } finally {
      setIsToggling(false);
    }
  };

  const confirmCopyPipeline = (pipelineId, pipelineName) => {
    setModalConfig({
      isOpen: true,
      title: t('dashboard.actions.copy_title') || 'Copiar Pipeline',
      message: `${t('dashboard.actions.copy_msg') || 'ΒΏEstΓ‘s seguro de crear una copia exacta de'} "${pipelineName}"?`,
      showCancel: true,
      onAccept: () => executeCopyPipeline(pipelineId)
    });
  };

  // π’ COPIAR PIPELINE
  const executeCopyPipeline = async (pipelineId) => {
    setIsCopying(true);
    setModalConfig({
      isOpen: true,
      title: t('dashboard.actions.copying_title'),
      message: t('dashboard.actions.copying_msg'),
      showCancel: false,
      onAccept: null
    });

    try {
      const response = await apiClient.post(`/dashboard/pipelines/${pipelineId}/copy`);
      if (response.data.success) {
        setModalConfig({
          isOpen: true,
          title: t('dashboard.actions.copy_success_title'),
          message: t('dashboard.actions.copy_success_msg'),
          showCancel: false,
          onAccept: closeModal
        });
        fetchPipelines();
      }
    } catch (err) {
      setModalConfig({
        isOpen: true,
        title: t('common.error'),
        message: err.message || t('dashboard.errors.copy_failed'),
        showCancel: false,
        onAccept: closeModal
      });
    } finally {
      setIsCopying(false);
    }
  };

  // const confirmForceExecute = (pipelineId, pipelineName, isEphemeral) => {
  const confirmForceExecute = (row) => {
    const { id: pipelineId, name: pipelineName, srcIsEphemeral, status, parentName, parentLastLog } = row;

    // Efímero
    if (srcIsEphemeral) {
      setModalConfig({ isOpen: true, title: t('dashboard.actions.ephemeral_title'), message: t('dashboard.actions.ephemeral_msg'), showCancel: false, onAccept: closeModal });
      return;
    }

    // WAITING con padre FAILED rows=0 → solo opción de ejecutar padre
    if (status === 3 && parentLastLog?.status === 0 && parentLastLog?.rowsProcessed === 0) {
      setModalConfig({
        isOpen: true,
        title: t('dashboard.status.waiting') || '⛔ Pipeline bloqueado',
        message: `"${parentName}" ${t('dashboard.actions.waiting_blocked_msg') || 'no procesó ningún registro. El hijo no puede ejecutarse hasta que el padre complete exitosamente.'}`,
        showCancel: true,
        cancelLabel: t('common.buttons.cancel') || 'Cancelar',
        acceptLabel: `▶ ${t('dashboard.actions.force_parent') || 'Ejecutar padre ahora'}`,
        onAccept: () => executePipeline(row.dependsOn),
      });
      return;
    }

    // WAITING con padre FAILED rows>0 → usuario decide
    if (status === 3 && parentLastLog?.status === 0 && parentLastLog?.rowsProcessed > 0) {
      setModalConfig({
        isOpen: true,
        title: `⚠️ ${t('dashboard.status.waiting') || 'Pipeline en espera'}`,
        message: `"${parentName}" ${t('dashboard.actions.waiting_partial_msg') || 'terminó con errores parciales.'} ${t('dashboard.actions.waiting_rows') || 'Registros procesados:'} ${parentLastLog.rowsProcessed.toLocaleString()}.`,
        showCancel: true,
        cancelLabel: t('common.buttons.cancel') || 'Cancelar',
        acceptLabel: `⚡ ${t('dashboard.actions.force_child') || 'Ejecutar hijo de todas formas'}`,
        onAccept: () => executePipeline(pipelineId),
      });
      return;
    }

    // Normal (con info de checkpoint si existe)
    const resumeMsg = row.hasCheckpoint
      ? `\n\n${t('dashboard.checkpoint.resume_notice') || '⏸ Este pipeline tiene progreso guardado. Al ejecutarlo, retomará desde donde quedó.'} (${(row.checkpoint?.rowsWrittenSoFar || 0).toLocaleString()} ${t('dashboard.checkpoint.rows_saved') || 'filas escritas'})`
      : '';

    setModalConfig({
      isOpen: true,
      title: row.hasCheckpoint
        ? (t('dashboard.checkpoint.resume_title') || '⏸ Retomar ejecución')
        : t('dashboard.actions.force_title'),
      message: `${t('dashboard.actions.force_msg_1')} "${pipelineName}"? ${t('dashboard.actions.force_msg_2')}${resumeMsg}`,
      showCancel: true,
      onAccept: () => executePipeline(pipelineId),
    });
  };

  const executePipeline = async (pipelineId) => {
    setModalConfig({
      isOpen: true,
      title: t('dashboard.actions.starting_etl'),
      message: t('dashboard.actions.sending_queue'),
      showCancel: false,
      onAccept: null
    });

    try {
      const response = await apiClient.post(`/dashboard/pipelines/${pipelineId}/execute`);
      if (response.data.success) {
        setModalConfig({
          isOpen: true,
          title: t('dashboard.actions.queued_title'),
          message: t('dashboard.actions.queued_msg'),
          showCancel: false,
          onAccept: closeModal
        });
        setTimeout(() => { fetchPipelines(); }, 4000);
      }
    } catch (err) {
      console.error("Error forzando ejecuciΓ³n:", err);
      setModalConfig({
        isOpen: true,
        title: t('common.error'),
        message: err.message || t('dashboard.errors.force_failed'),
        showCancel: false,
        onAccept: closeModal
      });
    }
  };

  const handleOpenHistory = async (pipelineId) => {
    setIsLoading(true);
    try {
      const res = await apiClient.get(`/dashboard/pipelines/${pipelineId}/history`);
      if (res.data.success) {
        setHistoryData({
          pipeline: res.data.pipeline,
          logs: res.data.history
        });
        setShowHistory(true);
      }
    } catch (err) {
      console.error("Error cargando historial", err);
      setModalConfig({
        isOpen: true,
        title: t('common.error') || 'Error',
        message: err.message || t('dashboard.errors.history_failed') || 'No se pudo cargar el historial de este pipeline.',
        showCancel: false,
        onAccept: closeModal
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenProfile = async (row) => {
    const tableName = row.sourceTable || '';
    if (!tableName) {
      setModalConfig({
        isOpen: true,
        title: t('common.error'),
        message: t('profiling.no_source_table') || 'No se encontró la tabla de origen en este pipeline.',
      });
      return;
    }
    // OBS-09: verificar en tiempo real (no depender del estado cacheado)
    try {
      const tvRes = await apiClient.get('/client/top-values-enabled');
      if (!tvRes.data?.enabled) {
        setModalConfig({
          isOpen: true,
          title: t('profiling.top_values_disabled_title') || 'Visualización de datos reales desactivada',
          message: t('profiling.top_values_disabled_msg') || 'Por su protección, la visualización de datos reales (valores más frecuentes) está desactivada por defecto. Para activarla, vaya a Mi Cuenta > Plan y active la opción "Visualizar datos reales en perfilado". El cliente es responsable del cumplimiento normativo al activar esta función.',
          showCancel: false,
        });
        return;
      }
    } catch { /* si falla el check, permitir acceso */ }
    setProfileModal({
      idConfigHead: row.id,
      engine: row.sourceType,
      tableName,
    });
  };

  const calculateSuccessRate = (success, errors) => {
    const total = success + errors;
    if (total === 0) return 0;
    return ((success / total) * 100).toFixed(2);
  };
  const calculateEffectiveness = (totalRecords, totalSeconds) => {
    if (totalSeconds === 0) return 0;
    return (totalRecords / totalSeconds).toFixed(1);
  };

  const getStatusDotColor = (pipeline) => {
    if (!pipeline) return 'bg-slate-300';
    if (pipeline.isRunning === 1) return 'bg-blue-500 animate-pulse';
    if (pipeline.status === 3) return 'bg-amber-400';
    if (pipeline.status === 1) return 'bg-emerald-500';
    if (pipeline.status === 2) return 'bg-amber-500';
    return 'bg-slate-300';
  };

  const getStatusLabel = (pipeline) => {
    if (!pipeline) return '';
    if (pipeline.isRunning === 1) return t('dashboard.status.running') || 'Ejecutando';
    if (pipeline.status === 3) return t('dashboard.status.waiting') || 'En espera';
    if (pipeline.status === 1) return t('dashboard.status.active') || 'Activo';
    if (pipeline.status === 2) return t('dashboard.status.paused') || 'Pausado';
    return t('dashboard.status.annulled') || 'Anulado';
  };

  const toggleParent = (id) => {
    setPendingDependsOnIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const saveDependsOn = async (childId, parentIds) => {
    try {
      await apiClient.patch(`/dashboard/pipelines/${childId}/depends-on`, {
        dependsOn: parentIds?.length ? parentIds : null,
        failMode: pendingFailMode,

      });

      setStatusFilter('all');   // resetear filtro para ver la cadena completa
      setDependsOnModal(null);
      setTimeout(() => fetchPipelines(), 300);
    } catch (err) {
      console.error('Error guardando dependencia:', err);
    }
  };

  // ── RES.4: Componente de barra de progreso inline ─────────────────
  const ProgressBar = ({ pipelineId }) => {
    const prog = progressMap[pipelineId];
    if (!prog || !prog.running) return null;

    const now = Date.now();
    const staleMs = now - (prog.lastUpdateAt || 0);
    const isStale = staleMs > 120_000; // 2 min sin update

    const pct = prog.percentComplete;
    const eta = prog.estimatedSecondsRemaining;

    const etaText = eta != null
      ? eta >= 60
        ? `~${Math.ceil(eta / 60)} min`
        : `~${eta}s`
      : null;

    return (
      <div className="mt-1.5 w-full max-w-[160px]">
        <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
          {pct != null ? (
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          ) : (
            <div className="h-full bg-blue-400 rounded-full animate-pulse w-full opacity-40" />
          )}
        </div>
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[9px] text-slate-500 font-mono">
            {(prog.rowsProcessed || 0).toLocaleString()} {t('dashboard.progress.rows') || 'filas'}
            {pct != null && ` (${pct}%)`}
          </span>
          {etaText && !isStale && (
            <span className="text-[9px] text-blue-500 font-medium">{etaText}</span>
          )}
        </div>
        {isStale && (
          <p className="text-[9px] text-amber-600 font-medium mt-0.5 flex items-center gap-0.5">
            <AlertTriangle className="w-2.5 h-2.5" />
            {t('dashboard.progress.stale') || 'Sin actividad reciente'}
          </p>
        )}
      </div>
    );
  };

  // π’ ESTA ES LA FUNCIΓN, VA AFUERA DEL HTML
  const getStatusBadge = (row) => {
    if (row.isRunning === 1)
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-800 uppercase tracking-wider animate-pulse">
        <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> {t('dashboard.status.running') || 'Ejecutando'}
      </span>;
    if (row.hasCheckpoint)
      return (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-100 text-violet-800 uppercase tracking-wider cursor-help"
          title={`${t('dashboard.checkpoint.tooltip') || 'Progreso guardado'}: ${(row.checkpoint?.rowsWrittenSoFar || 0).toLocaleString()} ${t('dashboard.checkpoint.rows_saved') || 'filas escritas'}`}
        >
          <RotateCcw className="w-3 h-3 mr-0.5" /> {t('dashboard.checkpoint.resumable') || 'Reanudable'}
        </span>
      );
    if (row.status === 3)
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wider">
        <Clock className="w-3 h-3 mr-0.5" /> {t('dashboard.status.waiting') || 'En espera'}
      </span>;
    if (row.status === 1)
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 uppercase tracking-wider">
        <CheckCircle2 className="w-3 h-3 mr-0.5" /> {t('dashboard.status.active') || 'Activo'}
      </span>;
    if (row.status === 2)
      return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 uppercase tracking-wider">
        <Pause className="w-3 h-3 mr-0.5" /> {t('dashboard.status.paused') || 'Pausado'}
      </span>;
    return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-800 uppercase tracking-wider">
      <XCircle className="w-3 h-3 mr-0.5" /> {t('dashboard.status.annulled') || 'Anulado'}
    </span>;
  };


  const activeCount = pipelines.filter(p => p.status === 1).length;
  const pausedCount = pipelines.filter(p => p.status === 2).length;
  const totalOperative = activeCount + pausedCount;
  const displayQuota = subscription.quota > 99999 ? t('dashboard.kpi.unlimited') || "Ilimitado" : subscription.quota;
  // π Detecta infracciΓ³n contractual: activos > cuota permitida.
  // Requiere subscription.quota vΓ‘lido (>0) y no ilimitado (99999+).
  const isOverQuota = subscription.quota > 0 && subscription.quota <= 99999 && activeCount > subscription.quota;

  const currentTotal = kpis.current7d.success + kpis.current7d.errors;
  const currentRate = calculateSuccessRate(kpis.current7d.success, kpis.current7d.errors);
  const currentEffectiveness = calculateEffectiveness(currentTotal, kpis.current7d.timeSec);
  const prevTotal = kpis.prev7d.success + kpis.prev7d.errors;
  const prevRate = calculateSuccessRate(kpis.prev7d.success, kpis.prev7d.errors);
  const prevEffectiveness = calculateEffectiveness(prevTotal, kpis.prev7d.timeSec);


  // Normalizar dependsOn: null / "null" / [] → []
  const _deps = (p) => {
    const d = p?.dependsOn;
    if (!d || d === 'null') return [];
    if (Array.isArray(d)) return d;
    return [];
  };

  const filteredPipelines = pipelines.filter(p => {
    if (statusFilter === 'running') return p.isRunning === 1;
    if (statusFilter === 'waiting') return p.status === 3;
    if (statusFilter === 'drift') return !!p.schemaDrift;
    if (statusFilter === 'all') return true;
    return p.status === Number(statusFilter);
  });

  // Lista plana ordenada — sin agrupación padre/hijo (evita duplicados)
  const sortedPipelines = (() => {
    const dir = sortAsc ? 1 : -1;
    const compareFn = (a, b) => {
      if (sortBy === 'nextExecution') return dir * (a.nextExecutionAt - b.nextExecutionAt);
      if (sortBy === 'name') return dir * a.name.localeCompare(b.name);
      if (sortBy === 'id') return dir * (a.id - b.id);
      if (sortBy === 'lastExecution') return dir * (a.lastExecutionAt - b.lastExecutionAt);
      return 0;
    };
    return [...filteredPipelines].sort(compareFn);
  })();

  const totalPages = Math.ceil(sortedPipelines.length / itemsPerPage) || 1;

  // Si la página actual excede el total (ej: cambio de cuenta con menos pipelines),
  // resetear a la última página válida
  if (currentPage > totalPages) {
    setCurrentPage(totalPages);
    sessionStorage.setItem('vnext_dash_page', String(totalPages));
  }

  const safePage = Math.min(currentPage, totalPages);
  const currentPipelines = sortedPipelines.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      {/* ── Modal dependencia ──────────────────────────────────── */}
      {dependsOnModal && (() => {
        const child = dependsOnModal.pipeline;
        const candidates = pipelines.filter(p =>
          p.id !== child.id &&
          !(p.dependsOn || []).includes(child.id)  // excluir hijos directos
        );
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={(e) => { if (e.target === e.currentTarget) setDependsOnModal(null); }}
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
              <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-purple-600" />
                {t('dashboard.actions.set_dependency') || 'Configurar dependencia'}
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Pipeline: <span className="font-semibold text-slate-700">{child.name}</span>
              </p>

              {/* Botón limpiar selección */}
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-slate-500">
                  {t('dashboard.actions.depends_on_label') || 'Espera a estos pipelines'}
                </label>
                {pendingDependsOnIds.length > 0 && (
                  <button
                    onClick={() => setPendingDependsOnIds([])}
                    className="text-[10px] text-red-500 hover:text-red-700 font-medium"
                    title={t('dashboard.actions.clear_deps_tooltip') || 'Desmarca todas las dependencias'}
                  >
                    ✕ {t('dashboard.actions.independent') || 'Limpiar (Independiente)'}
                  </button>
                )}
              </div>

              {/* CheckSelect list */}
              <div className="border border-slate-200 rounded-lg overflow-hidden mb-4 max-h-52 overflow-y-auto">
                {candidates.length === 0 ? (
                  <p className="text-xs text-slate-400 p-3 text-center">
                    No hay otros pipelines disponibles
                  </p>
                ) : (
                  candidates.map(p => {
                    const checked = pendingDependsOnIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-slate-100 last:border-0 ${checked ? 'bg-purple-50' : 'hover:bg-slate-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleParent(p.id)}
                          className="w-4 h-4 rounded accent-purple-600 shrink-0"
                        />
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${getStatusDotColor(p)}`} />
                          <span className="text-sm font-medium text-slate-800 truncate" title={p.name}>
                            {p.name}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono shrink-0 ml-auto">
                            ({p.id}) {getStatusLabel(p)}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              {/* Preview seleccionados */}
              {pendingDependsOnIds.length > 0 && (
                <div className="mb-4 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-[10px] font-semibold text-purple-700 uppercase mb-1">
                    {t('dashboard.actions.current_parent') || 'Esperará a'} ({pendingDependsOnIds.length}):
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {pendingDependsOnIds.map(id => {
                      const pp = pipelines.find(x => x.id === id);
                      return pp ? (
                        <span key={id} className="inline-flex items-center gap-1 text-[10px] bg-white border border-purple-200 text-purple-700 font-medium px-2 py-0.5 rounded-full">
                          <div className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(pp)}`} />
                          ({id}) {pp.name.length > 15 ? pp.name.slice(0, 15) + '…' : pp.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {pendingDependsOnIds.length === 0 && (
                <p className="text-xs text-slate-400 mb-4 italic">
                  {t('dashboard.actions.no_parent_selected') || 'Sin selección → el pipeline correrá de forma independiente.'}
                </p>
              )}

              {/* ── Tip + failMode ─────────────────────────────────── */}
              {pendingDependsOnIds.length > 0 && (
                <div className="mb-4">
                  {/* Tip acordeón */}
                  <button
                    type="button"
                    onClick={() => setShowDepTip(prev => !prev)}
                    className="flex items-center gap-1.5 text-[11px] text-amber-600 hover:text-amber-700 mb-2"
                  >
                    <span>💡</span>
                    <span className="font-medium">
                      {t('dashboard.deps.tip_label') || '¿Cómo evalúa el sistema a los padres?'}
                    </span>
                    <span className="text-slate-400">{showDepTip ? '▲' : '▼'}</span>
                  </button>

                  {showDepTip && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-[11px] text-amber-800 leading-relaxed">
                      {pendingFailMode === 'strict' && (
                        <>
                          <p className="font-bold mb-1">🔒 {t('dashboard.deps.tip_strict_title') || 'Modo Estricto'}</p>
                          <p>{t('dashboard.deps.tip_strict_body') || 'Solo continúa si TODOS los padres terminaron al 100% sin errores. Cualquier fallo o resultado parcial bloqueará este pipeline.'}</p>
                        </>
                      )}
                      {pendingFailMode === 'normal' && (
                        <>
                          <p className="font-bold mb-1">✅ {t('dashboard.deps.tip_normal_title') || 'Modo Normal (default)'}</p>
                          <ul className="space-y-0.5 mt-1">
                            <li>✅ {t('dashboard.deps.tip_success') || 'Exitoso → continúa'}</li>
                            <li>✅ {t('dashboard.deps.tip_partial') || 'Algunos datos pasaron → continúa'}</li>
                            <li>⛔ {t('dashboard.deps.tip_partial_empty') || 'Ningún dato pasó → bloquea'}</li>
                            <li>⛔ {t('dashboard.deps.tip_failed') || 'Falló → bloquea (puedes forzar desde el Dashboard)'}</li>
                          </ul>
                        </>
                      )}
                      {pendingFailMode === 'permissive' && (
                        <>
                          <p className="font-bold mb-1">⚠️ {t('dashboard.deps.tip_permissive_title') || 'Modo Permisivo'}</p>
                          <p>{t('dashboard.deps.tip_permissive_body') || 'Este pipeline comenzará sin importar si fallaron las dependencias, siempre que no estén ejecutando.'}</p>
                        </>
                      )}
                    </div>
                  )}

                  {/* Radio buttons failMode */}
                  <div className="space-y-2">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">
                      {t('dashboard.deps.fail_mode_label') || 'Comportamiento si algún padre falla:'}
                    </p>
                    {[
                      { value: 'strict', label: t('dashboard.deps.mode_strict') || 'Estricto: solo continúa si todos terminaron al 100% sin errores' },
                      { value: 'normal', label: t('dashboard.deps.mode_normal') || 'Normal: continúa si algunos datos pasaron, bloquea si todos fallaron (default)' },
                      { value: 'permissive', label: t('dashboard.deps.mode_permissive') || 'Permisivo: continúa siempre, aunque los padres fallen' },
                    ].map(({ value, label }) => (
                      <label key={value} className="flex items-start gap-2 cursor-pointer group">
                        <input
                          type="radio"
                          name="failMode"
                          value={value}
                          checked={pendingFailMode === value}
                          onChange={() => setPendingFailMode(value)}
                          className="mt-0.5 accent-purple-600 shrink-0"
                        />
                        <span className={`text-[11px] leading-tight ${pendingFailMode === value ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setDependsOnModal(null)}
                  className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium"
                >
                  {t('common.buttons.cancel') || 'Cancelar'}
                </button>
                <button
                  onClick={() => saveDependsOn(child.id, pendingDependsOnIds)}
                  className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold"
                >
                  {t('common.buttons.accept') || 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">

          {/* Logo */}
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600
                        hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>

          {/* Acciones */}
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{clientName}</p>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>

        </div>
      </header>

      {showHistory ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in slide-in-from-right-4">
          <PipelineHistory
            pipeline={historyData.pipeline}
            historyLogs={historyData.logs}
            onBack={() => setShowHistory(false)}
            onRefresh={handleRefreshHistory}
            isRefreshing={isRefreshingHistory}
          />
        </main>
      ) : (

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <LayoutDashboard className="w-6 h-6 mr-2 text-indigo-600" /> {t('dashboard.title') || 'Centro de Control ETL'}
              </h1>
            </div>
            <Link to="/migracion" className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition flex items-center justify-center">
              <Plus className="w-5 h-5 mr-1" /> {t('dashboard.new_migration') || 'Nueva MigraciΓ³n'}
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

            {/* ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ
              KPI #1 β PIPELINES (con detecciΓ³n de overuse contractual)
              ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ */}
            <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center relative min-h-[110px] gap-3 sm:gap-0">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 border shrink-0 ${isOverQuota ? 'bg-red-50 border-red-100' : 'bg-indigo-50 border-indigo-100'}`}>
                  <Database className={`w-6 h-6 ${isOverQuota ? 'text-red-600' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">{t('dashboard.kpi.pipelines') || 'Pipelines'}</p>
                  <div className="flex items-baseline space-x-1.5">
                    <p className={`text-2xl font-black leading-none ${isOverQuota ? 'text-red-600' : 'text-slate-800'}`}>
                      {activeCount}
                    </p>
                    <p className="text-xs font-medium text-slate-400">/ {displayQuota}</p>
                    {isOverQuota && (
                      <span
                        className="ml-1 inline-flex items-center text-[9px] font-bold text-red-700 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 cursor-help"
                        title={t('dashboard.kpi.overuse_tt', { active: activeCount, limit: subscription.quota })}
                      >
                        <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                        {t('dashboard.kpi.overuse') || 'Sobre cuota'}
                      </span>
                    )}
                  </div>
                  {pausedCount > 0 && (
                    <p className="text-[10px] text-slate-400 mt-1 leading-none">
                      +{pausedCount} {t('dashboard.status.paused') || 'pausados'}
                    </p>
                  )}
                </div>
              </div>
              <div className="self-end sm:absolute sm:bottom-4 sm:right-5 flex flex-col items-start mt-auto">
                <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 mb-1">{subscription.planName}</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase leading-none ml-2">{t('dashboard.kpi.quota') || 'Cuota:'} {displayQuota}</span>
              </div>
            </div>

            {/* ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ
              KPI #2 β VOLUMEN / ERRORES (7 dΓ­as)
              ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ */}
            <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center relative min-h-[110px] gap-3 sm:gap-0">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mr-4 border border-emerald-100 shrink-0"><TrendingUp className="w-6 h-6 text-emerald-600" /></div>
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('dashboard.kpi.volume') || 'Volumen / Errores'} <span className="text-[9px] text-slate-400 normal-case">(7D)</span></p>
                  <div className="flex items-baseline flex-wrap gap-1.5">
                    <span className="text-2xl font-black text-slate-800 leading-none cursor-help" title={`${t('dashboard.kpi.success_tt') || 'Procesados con Γ©xito:'} ${kpis.current7d.success.toLocaleString('en-US')}`}>{formatCompact(kpis.current7d.success)}</span>
                    <span className="text-lg font-bold text-slate-400">/</span>
                    <span className="text-lg font-bold text-red-500 cursor-help" title={`${t('dashboard.kpi.error_tt') || 'Procesados con error:'} ${kpis.current7d.errors.toLocaleString('en-US')}`}>{formatCompact(kpis.current7d.errors)}</span>
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded cursor-help" title={`${t('dashboard.kpi.rate_tt') || 'Tasa de Γ©xito:'} (${kpis.current7d.success.toLocaleString('en-US')} / ${currentTotal.toLocaleString('en-US')}) * 100`}>({currentRate}%)</span>
                  </div>
                </div>
              </div>
              <div className="self-end sm:absolute sm:bottom-3 sm:right-4 flex flex-col items-end opacity-60 mt-auto">
                <span className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">{t('dashboard.kpi.prev_week') || 'Semana Anterior'}</span>
                <span className="text-[10px] font-bold text-slate-600 cursor-help" title={`${t('dashboard.kpi.success_tt')} ${kpis.prev7d.success.toLocaleString('en-US')} | ${t('dashboard.kpi.error_tt')} ${kpis.prev7d.errors.toLocaleString('en-US')}`}>
                  {formatCompact(kpis.prev7d.success)} / {formatCompact(kpis.prev7d.errors)} ({prevRate}%)
                </span>
              </div>
            </div>

            {/* ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ
              KPI #3 β THROUGHPUT / RENDIMIENTO (7 dΓ­as)
              ββββββββββββββββββββββββββββββββββββββββββββββββββββββββββββ */}
            <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-center relative min-h-[110px] gap-3 sm:gap-0">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mr-4 border border-blue-100 shrink-0"><Activity className="w-6 h-6 text-blue-600" /></div>
                <div>
                  <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t('dashboard.kpi.throughput') || 'Rendimiento (Throughput)'} <span className="text-[9px] text-slate-400 normal-case">(7D)</span></p>
                  <div className="flex items-baseline flex-wrap gap-1.5">
                    <span className="text-2xl font-black text-slate-800 leading-none cursor-help" title={`${t('dashboard.kpi.time_tt') || 'Tiempo total de procesamiento:'} ${kpis.current7d.timeSec.toLocaleString('en-US')} seg`}>{formatTime(kpis.current7d.timeSec)}</span>
                    <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded cursor-help" title={`${t('dashboard.kpi.eff_tt') || 'Tasa de rendimiento:'} ${currentTotal.toLocaleString('en-US')} filas / ${kpis.current7d.timeSec} seg`}>
                      ({Number(currentEffectiveness).toLocaleString('en-US')} r/s)
                    </span>
                  </div>
                </div>
              </div>
              <div className="self-end sm:absolute sm:bottom-3 sm:right-4 flex flex-col items-end opacity-60 mt-auto">
                <span className="text-[9px] font-bold text-slate-400 uppercase mb-0.5">{t('dashboard.kpi.prev_week') || 'Semana Anterior'}</span>
                <span className="text-[10px] font-bold text-slate-600 cursor-help" title={`Tiempo: ${kpis.prev7d.timeSec}s | Rendimiento: ${Number(prevEffectiveness).toLocaleString('en-US')} r/s`}>
                  {formatTime(kpis.prev7d.timeSec)} ({Number(prevEffectiveness).toLocaleString('en-US')} r/s)
                </span>
              </div>
            </div>

          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row justify-between md:items-center gap-4">
              <h3 className="font-bold text-slate-800 flex items-center">
                <Server className="w-5 h-5 mr-2 text-indigo-500" /> {t('dashboard.table.list_title') || 'Listado de Procesos'}
              </h3>

              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 w-full md:w-auto">
                {/* Orden + toggle ASC/DESC */}
                <div className="flex items-center bg-white border border-slate-200 rounded-lg px-2 py-1.5 shadow-sm flex-1 sm:flex-none gap-1">
                  <button
                    onClick={() => setSortAsc(prev => !prev)}
                    className="text-slate-400 hover:text-indigo-600 transition"
                    title={sortAsc ? 'Ascendente' : 'Descendente'}
                  >
                    {sortAsc
                      ? <ArrowUpDown className="w-3.5 h-3.5 text-indigo-500" />
                      : <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                    }
                  </button>
                  <select
                    className="text-[11px] sm:text-xs font-bold text-slate-600 outline-none bg-transparent cursor-pointer w-full"
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="nextExecution">{t('dashboard.table.sort_sched') || 'Programación'}</option>
                    <option value="name">          {t('dashboard.table.sort_name') || 'Nombre'}</option>
                    <option value="id">ID</option>
                    <option value="lastExecution">{t('dashboard.table.sort_last') || 'Última actividad'}</option>
                  </select>
                </div>

                {/* Filtro por estado */}
                <div className="flex items-center bg-white border border-slate-200 rounded-lg px-2 py-1.5 shadow-sm flex-1 sm:flex-none">
                  <Filter className="w-3.5 h-3.5 text-slate-400 mr-1.5" />
                  <select
                    className="text-[11px] sm:text-xs font-bold text-slate-600 outline-none bg-transparent cursor-pointer w-full"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="all">     {t('dashboard.table.filter_all') || 'Todos'}</option>
                    <option value="1">       {t('dashboard.status.active') || 'Activos'}</option>
                    <option value="2">       {t('dashboard.status.paused') || 'Pausados'}</option>
                    <option value="0">       {t('dashboard.status.annulled') || 'Anulados'}</option>
                    <option value="running"> {t('dashboard.status.running') || 'En ejecución'}</option>
                    <option value="waiting"> {t('dashboard.status.waiting') || 'En espera'}</option>
                    <option value="drift">   {t('sync_guardian.filter_drift') || 'Con diferencia de esquema'}</option>
                  </select>
                </div>
                <button onClick={fetchPipelines} className="p-1.5 sm:p-2 flex items-center justify-center text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 rounded-lg transition shadow-sm ml-auto" title={t('dashboard.table.refresh')}>
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center p-12 text-slate-400"><Loader2 className="w-8 h-8 animate-spin mb-3 text-indigo-600" /><p>{t('dashboard.loading_pipes') || 'Cargando pipelines...'}</p></div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center p-12 text-red-500 bg-red-50"><AlertTriangle className="w-8 h-8 mb-3" /><p className="font-bold text-sm">{error}</p></div>
              ) : sortedPipelines.length === 0 ? (
                <div className="text-center p-12">
                  <Database className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-700">{t('dashboard.empty.title') || 'No hay pipelines aquΓ­'}</h3>
                  <p className="text-slate-500 mt-1 max-w-sm mx-auto text-sm">{t('dashboard.empty.desc') || 'Crea tu primer pipeline para empezar a mover datos automΓ‘ticamente.'}</p>
                  <Link to="/migracion" className="mt-5 inline-flex items-center px-4 py-2 bg-indigo-50 text-indigo-700 font-bold rounded-lg hover:bg-indigo-100 transition">
                    {t('dashboard.empty.btn') || 'Crear Primera MigraciΓ³n'}
                  </Link>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead className="bg-white border-b border-slate-100">
                    <tr>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">Pipeline</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.connections') || 'Conexiones'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.sched') || 'ProgramaciΓ³n'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.next') || 'PrΓ³x. Activ.'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.last') || 'Ult. Activ.'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.processed') || 'Procesado (7D)'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t('dashboard.table.cols.performance') || 'Rendimiento (7D)'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">{t('dashboard.table.cols.health') || 'Salud'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">{t('dashboard.table.cols.status') || 'Estado'}</th>
                      <th className="px-1.5 py-2 text-[9px] font-bold text-slate-400 uppercase tracking-wider text-right">{t('dashboard.table.cols.actions') || 'Acciones'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {currentPipelines.map((row) => {
                      const rowTotal = (row.stats7d?.success || 0) + (row.stats7d?.errors || 0);
                      const rowRate = calculateSuccessRate(row.stats7d?.success || 0, row.stats7d?.errors || 0);
                      const rowEff = calculateEffectiveness(rowTotal, row.stats7d?.timeSec || 0);
                      return (
                        <React.Fragment key={row.id}>
                          <tr className={`hover:bg-slate-50/50 transition-colors ${row.status === 0 ? 'opacity-60 bg-slate-50/30' : ''}`}>
                            <td className="px-1.5 py-2">
                              <div className={`w-2 h-2 rounded-full mr-2 shrink-0 mt-1.5 ${row.isRunning === 1 ? 'bg-blue-500 animate-pulse' :
                                row.status === 3 ? 'bg-amber-400' :
                                  row.status === 1 ? 'bg-emerald-500' :
                                    row.status === 2 ? 'bg-amber-500' : 'bg-slate-300'
                                }`}></div>
                              <div className="flex items-start">
                                <div className="min-w-0">
                                  <p className="text-[13px] text-slate-700 truncate max-w-[140px]" title={row.name}>
                                    {row.name}
                                  </p>
                                  {/* <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {row.id}</p> */}
                                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">ID: {row.id}</p>
                                  {row.idProject && (
                                    <Link
                                      to={`/projects/${row.idProject}`}
                                      className="inline-flex items-center gap-1 mt-1 text-[9px] font-bold text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded hover:bg-indigo-100 transition-colors max-w-[140px]"
                                      title={`Ver proyecto #${row.idProject}${row.idProjectPhase ? ` · Fase orden ${row.phaseOrder ?? '?'}` : ''}`}
                                    >
                                      <FolderKanban className="w-2.5 h-2.5 shrink-0" />
                                      <span className="truncate">
                                        Proy.{row.idProject}
                                        {row.idProjectPhase && ` · F${row.phaseOrder ?? '?'}`}
                                      </span>
                                    </Link>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="px-1.5 py-2">
                              <div className="text-[11px] space-y-1 min-w-0">
                                <div className="flex items-center text-slate-500 truncate max-w-[150px]"
                                  title={row.sourceTableTooltip || row.sourceTable}>
                                  <span className="font-bold mr-1 text-slate-600">Or:</span>
                                  {row.sourceType} {row.sourceTable ? `(${row.sourceTable})` : ''}
                                </div>
                                <div className="flex items-center text-slate-500 truncate max-w-[150px]"
                                  title={row.targetTableTooltip || row.targetTable}>
                                  <span className="font-bold mr-1 text-slate-600">De:</span>
                                  {row.targetType} {row.targetTable ? `(${row.targetTable})` : ''}
                                </div>
                              </div>
                            </td>

                            <td className="px-1.5 py-2">
                              <div className="text-[11px]">
                                <p className="font-bold text-slate-600 flex items-center cursor-help" title={`CRON: ${row.cron || 'N/A'}`}>
                                  <Clock className="w-3 h-3 mr-1 text-slate-400" />
                                  {translateCron(row.cron, row.frequency)}
                                </p>
                                {row.startDate && (
                                  <span className="text-[9px] text-slate-500 block mt-1 leading-tight">
                                    {t('dashboard.table.from')} {row.startDate} {t('dashboard.table.to')} {row.endDate || t('wizard.review.schedule.no_limit')}
                                  </span>
                                )}
                                {row.timeWindow && (
                                  <span className="text-[9px] text-slate-500 block mt-0.5 leading-tight">
                                    {t('dashboard.table.time_window')} {row.timeWindow}
                                  </span>
                                )}
                              </div>
                            </td>

                            <td className="px-1.5 py-2">
                              <div className="text-[11px]">
                                {row.status === 1 ? (
                                  <>
                                    <span className="font-bold text-indigo-600 block leading-tight">{formatUnixDate(row.nextExecutionAt, { dateOnly: true })}</span>
                                    <span className="font-medium text-indigo-500/80 block text-[10px] leading-tight">{formatUnixDate(row.nextExecutionAt, { timeOnly: true, hideSeconds: true })}</span>
                                  </>
                                ) : (
                                  <>
                                    <span className="font-medium text-slate-400 italic block leading-tight">{formatUnixDate(row.nextExecutionAt, { dateOnly: true })}</span>
                                    <span className="text-[10px] text-slate-400/80 block leading-tight">{formatUnixDate(row.nextExecutionAt, { timeOnly: true, hideSeconds: true })}</span>
                                    <span className="text-[9px] text-slate-400/60 italic">({t('dashboard.status.paused')})</span>
                                  </>
                                )}
                              </div>
                            </td>

                            <td className="px-1.5 py-2">
                              <div className="text-[11px]">
                                {row.lastExecutionAt === 0 ? (
                                  <span className="text-slate-400 italic">{t('dashboard.table.no_exec') || 'Sin ejecuciones'}</span>
                                ) : (
                                  <>
                                    <span className="font-medium text-slate-600 block leading-tight">{formatUnixDate(row.lastExecutionAt, { dateOnly: true })}</span>
                                    <span className="font-medium text-slate-500/80 block text-[10px] leading-tight">{formatUnixDate(row.lastExecutionAt, { timeOnly: true })}</span>
                                    <span className="text-[9px] text-slate-400/70 block leading-tight">{row.timezone}</span>
                                  </>
                                )}
                              </div>
                            </td>

                            <td className="px-1.5 py-2 text-xs">
                              {rowTotal === 0 ? <span className="text-slate-300 italic">-</span> : (
                                <div className="flex items-center space-x-2">
                                  <div>
                                    <div className="flex items-center text-emerald-600 font-bold mb-0.5 cursor-help" title={`${t('dashboard.kpi.success_tt')} ${row.stats7d.success.toLocaleString('en-US')}`}>
                                      <CheckCircle2 className="w-3 h-3 mr-1" /> {formatCompact(row.stats7d.success)}
                                    </div>
                                    {row.stats7d.errors > 0 && (
                                      <div className="flex items-center text-red-500 font-bold cursor-help" title={`${t('dashboard.kpi.error_tt')} ${row.stats7d.errors.toLocaleString('en-US')}`}>
                                        <XCircle className="w-3 h-3 mr-1" /> {formatCompact(row.stats7d.errors)}
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded cursor-help" title={t('dashboard.kpi.rate_tt')}>({rowRate}%)</span>
                                </div>
                              )}
                            </td>

                            <td className="px-1.5 py-2 text-xs">
                              {rowTotal === 0 ? <span className="text-slate-300 italic">-</span> : (
                                <div className="flex flex-col space-y-0.5">
                                  <span className="font-bold text-slate-700 cursor-help" title={`${t('dashboard.kpi.time_tt')} ${row.stats7d.timeSec.toLocaleString('en-US')}s`}>{formatTime(row.stats7d.timeSec)}</span>
                                  <span className="text-[10px] font-bold text-blue-600 cursor-help" title={`${rowTotal.toLocaleString('en-US')} filas procesadas en ${row.stats7d.timeSec} seg`}>{Number(rowEff).toLocaleString('en-US')} r/s</span>
                                </div>
                              )}
                            </td>

                            <td className="px-1.5 py-2 text-center align-middle">
                              <HealthBadge data={row.healthScore} t={t} />
                            </td>

                            <td className="px-1.5 py-2 text-center align-middle">
                              {getStatusBadge(row)}
                              {row.isRunning === 1 && <ProgressBar pipelineId={row.id} />}
                              {row.schemaDrift && (
                                <button
                                  onClick={() => setDriftModal({ name: row.name, schemaDrift: row.schemaDrift })}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-800 uppercase tracking-wider mt-1 cursor-pointer hover:bg-orange-200 transition"
                                  title={t('sync_guardian.badge_tooltip') || 'Cambio de esquema detectado. Click para ver detalle.'}
                                >
                                  <ShieldAlert className="w-3 h-3 mr-0.5" />
                                  {t('sync_guardian.badge') || 'Schema drift'}
                                </button>
                              )}
                            </td>

                            <td className="px-1.5 py-2 text-right align-middle">
                              <div className="flex items-center justify-end space-x-0.5 min-w-max">

                                <button onClick={() => handleOpenHistory(row.id)} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title={t('dashboard.actions.history_btn')}><History className="w-4 h-4" /></button>

                                <button
                                  onClick={() => handleOpenProfile(row)}
                                  className="p-1 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                  title={t('profiling.title') || 'Data Profiling'}
                                >
                                  <BarChart3 className="w-4 h-4" />
                                </button>

                                <button
                                  onClick={() => handleOpenLineage(row.id, apiClient, setLineageModal, t, setModalConfig, closeModal)}
                                  className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition relative"
                                  title={t('lineage.title') || 'Data Lineage'}
                                >
                                  <GitCommitHorizontal className="w-4 h-4" />
                                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full ring-2 ring-white" />
                                </button>

                                <button
                                  onClick={() => confirmCopyPipeline(row.id, row.name)}
                                  disabled={isCopying}
                                  className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition disabled:opacity-50"
                                  title={t('dashboard.actions.copy_btn') || 'Clonar Pipeline'}
                                >
                                  <Copy className="w-4 h-4" />
                                </button>

                                <div className="relative group/export">
                                  <button
                                    className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title={t('dashboard.actions.export_mapping') || 'Exportar Mapping'}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-xl border border-slate-100 opacity-0 invisible group-hover/export:opacity-100 group-hover/export:visible transition-all z-50 overflow-hidden">
                                    <button
                                      onClick={() => handleExportMapping(row.id, row.name, 'json')}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition font-medium"
                                    >
                                      📄 {t('dashboard.actions.export_json') || 'Exportar JSON'}
                                    </button>
                                    <button
                                      onClick={() => handleExportMapping(row.id, row.name, 'excel')}
                                      className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition font-medium"
                                    >
                                      📊 {t('dashboard.actions.export_excel') || 'Exportar Excel'}
                                    </button>
                                  </div>
                                </div>

                                <button onClick={() => navigate(`/migracion?id=${row.id}`)} className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title={t('dashboard.actions.edit_btn')}><Settings2 className="w-4 h-4" /></button>

                                {row.status === 1 && <button onClick={() => handleToggleStatus(row.id)} disabled={isToggling} className="p-1 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition disabled:opacity-50" title={t('dashboard.actions.pause_btn')}><Pause className="w-4 h-4" /></button>}
                                {row.status === 2 && <button onClick={() => handleToggleStatus(row.id)} disabled={isToggling} className="p-1 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:opacity-50" title={t('dashboard.actions.activate_btn')}><Play className="w-4 h-4" /></button>}
                                <button
                                  onClick={() => setDependsOnModal({ pipeline: row })}
                                  className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition"
                                  title={t('dashboard.actions.set_dependency') || 'Configurar dependencia'}
                                >
                                  <GitBranch className="w-4 h-4" />
                                </button>
                                {row.status !== 0 && (
                                  <button
                                    // onClick={() => confirmForceExecute(row.id, row.name, row.srcIsEphemeral)}
                                    onClick={() => confirmForceExecute(row)}

                                    className={`border px-1.5 py-1 rounded-md text-[10px] font-bold flex items-center transition shadow-sm

                                    ${row.srcIsEphemeral
                                        ? 'bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                      }`}
                                    title={row.srcIsEphemeral
                                      ? t('dashboard.actions.ephemeral_tt') || 'Archivo de origen ya eliminado (ZDR)'
                                      : t('dashboard.actions.force_tt')
                                    }
                                  >
                                    <Play className="w-3 h-3 mr-1" fill="currentColor" />
                                    {t('dashboard.actions.force_btn')}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* ── Fila: soy hijo de estos padres ── */}
                          {(() => {
                            const depIds = _deps(row);
                            if (!depIds.length) return null;
                            const names = row.parentNames || [];
                            const rowTooltip = names.length
                              ? `${t('dashboard.deps.tooltip_parents') || 'Para comenzar este proceso deben concluir'}: ${names.map(n => `(${n.id}) ${n.name}`).join(', ')}`
                              : '';
                            return (
                              <tr className={row.status === 0 ? 'opacity-60' : ''}>
                                <td colSpan={9} className="px-3 pb-1.5 pt-0" title={rowTooltip}>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <IconChildOf className="w-4 h-3.5 text-indigo-400 shrink-0" />
                                    {depIds.map((pid, i) => {
                                      const pp = pipelines.find(x => x.id === pid);
                                      const pName = names.find(x => x.id === pid)?.name || `#${pid}`;
                                      const short = pName.length > 18 ? pName.slice(0, 18) + '…' : pName;
                                      const chipTooltip = `(${pid}) ${pName} — ${getStatusLabel(pp)}`;
                                      return (
                                        <span key={pid} className="inline-flex items-center gap-1 shrink-0" title={chipTooltip}>
                                          <div className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(pp)}`} />
                                          <span className="text-[9px] text-slate-500 font-mono">({pid}) {short}</span>
                                          {i < depIds.length - 1 && <span className="text-slate-200 text-[9px] mx-1">·</span>}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}

                          {/* ── Fila: soy padre de estos hijos ── */}
                          {(() => {
                            const myKids = pipelines.filter(p => _deps(p).includes(row.id));
                            if (!myKids.length) return null;
                            const rowTooltip = `${t('dashboard.deps.tooltip_children') || 'Al terminar este proceso podrán iniciar'}: ${myKids.map(c => `(${c.id}) ${c.name}`).join(', ')}`;
                            return (
                              <tr className={row.status === 0 ? 'opacity-60' : ''}>
                                <td colSpan={9} className="px-3 pb-2 pt-0" title={rowTooltip}>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <IconParentOf className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                    {myKids.map((c, i) => {
                                      const short = c.name.length > 18 ? c.name.slice(0, 18) + '…' : c.name;
                                      const chipTooltip = `(${c.id}) ${c.name} — ${getStatusLabel(c)}`;
                                      return (
                                        <span key={c.id} className="inline-flex items-center gap-1 shrink-0" title={chipTooltip}>
                                          <div className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(c)}`} />
                                          <span className="text-[9px] text-slate-500 font-mono">({c.id}) {short}</span>
                                          {i < myKids.length - 1 && <span className="text-slate-200 text-[9px] mx-1">·</span>}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            );
                          })()}

                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              )}

              {!isLoading && sortedPipelines.length > itemsPerPage && (
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                  <p className="text-xs text-slate-500 font-medium">
                    {t('dashboard.table.showing')} <span className="font-bold">{((currentPage - 1) * itemsPerPage) + 1}</span> {t('dashboard.table.to')} <span className="font-bold">{Math.min(currentPage * itemsPerPage, sortedPipelines.length)}</span> {t('dashboard.table.of')} <span className="font-bold">{sortedPipelines.length}</span>
                  </p>
                  <div className="flex space-x-2">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-1.5 rounded bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-1.5 rounded bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      )}
      {/* ── 🛡️ Sync Guardian: Modal de Schema Drift ────────────────── */}
      {driftModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={() => setDriftModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-orange-50">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-orange-600" />
                <h3 className="text-sm font-bold text-orange-900">{t('sync_guardian.modal_title') || 'Cambio de esquema detectado'}</h3>
              </div>
              <button onClick={() => setDriftModal(null)} className="text-slate-400 hover:text-slate-600 transition">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto max-h-[60vh]">
              <p className="text-xs text-slate-500 mb-3">
                {t('sync_guardian.modal_desc') || 'Se detectaron cambios en la estructura de las tablas de este pipeline. El pipeline se ejecutó normalmente. Revisa si el mapping necesita actualización.'}
              </p>
              <p className="text-xs font-bold text-slate-700 mb-2">{driftModal.name}</p>
              <div className="space-y-1.5">
                {(driftModal.schemaDrift?.drifts || []).map((d, i) => {
                  const typeColors = {
                    COLUMN_ADDED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
                    COLUMN_REMOVED: 'bg-red-50 text-red-800 border-red-200',
                    TYPE_CHANGED: 'bg-amber-50 text-amber-800 border-amber-200',
                    NULLABLE_CHANGED: 'bg-blue-50 text-blue-800 border-blue-200',
                  };
                  const typeLabels = {
                    COLUMN_ADDED: t('sync_guardian.drift_added') || 'Columna nueva',
                    COLUMN_REMOVED: t('sync_guardian.drift_removed') || 'Columna eliminada',
                    TYPE_CHANGED: t('sync_guardian.drift_type') || 'Tipo cambió',
                    NULLABLE_CHANGED: t('sync_guardian.drift_nullable') || 'Nulabilidad cambió',
                  };
                  const sideLabel = d.side === 'source'
                    ? (t('sync_guardian.side_source') || 'Origen')
                    : (t('sync_guardian.side_target') || 'Destino');
                  return (
                    <div key={i} className={`flex items-start gap-2 px-3 py-2 rounded-lg border text-[11px] ${typeColors[d.driftType] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                      <span className="font-bold shrink-0">{sideLabel}</span>
                      <span className="font-mono">{d.table}.{d.column}</span>
                      <span className="ml-auto font-bold whitespace-nowrap">{typeLabels[d.driftType] || d.driftType}</span>
                      {d.detail && <span className="text-[10px] opacity-70">({d.detail})</span>}
                    </div>
                  );
                })}
              </div>
              {driftModal.schemaDrift?.detectedAt && (
                <p className="text-[10px] text-slate-400 mt-3">
                  {t('sync_guardian.detected_at') || 'Detectado'}: {new Date(driftModal.schemaDrift.detectedAt).toLocaleString()}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
              <button onClick={() => setDriftModal(null)} className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition">
                {t('common.close') || 'Cerrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        onClose={closeModal}
        onAccept={modalConfig.onAccept}
        showCancel={modalConfig.showCancel}
      />

      {/* 🧬 Sprint 13.1: Data Lineage Modal */}
      {lineageModal && (
        <DataLineageModal
          data={lineageModal.data}
          onClose={() => setLineageModal(null)}
        />
      )}
      {/* 🆕 S5: Data Profiling Modal */}
      {profileModal && (
        <ProfileModal
          isOpen={true}
          onClose={() => setProfileModal(null)}
          sourceConfig={{
            engine: profileModal.engine,
            config: {},
          }}
          tableName={profileModal.tableName}
          idConfigHead={profileModal.idConfigHead}
          onApplySuggestions={null}
        />
      )}

      {/* Drawer de navegación global */}
      <AppSidebar user={user} onLogout={handleLogout} />

    </div>
  );
};
